<template>
  <div class="ptz">
    <div class="ptz__head">
      <span class="ptz__title"><i class="el-icon-s-operation"></i> 云台控制</span>
      <span class="ptz__sub">{{ nvrLabel }} · CH{{ channel }}</span>
    </div>

    <div class="ptz__notice" v-if="!capLoading && ptzSupported === false">
      <i class="el-icon-info"></i> 该通道不支持云台控制
    </div>

    <div class="ptz__pads">
      <div class="ptz__grid">
        <el-button v-for="d in dirs" :key="d.key" size="small"
                   class="ptz__btn"
                   :disabled="!ptzSupported || capLoading"
                   :class="{ 'is-holding': holding && holdCmd && holdCmd.key === d.key }"
                   :icon="d.icon"
                   @pointerdown.native.prevent="pressStart(d)"
                   @pointerup.native.prevent="onRelease"
                   @pointercancel.native="onRelease"
                   @pointerleave.native="onRelease">
          {{ d.short }}
        </el-button>
      </div>
      <div class="ptz__zoom">
        <el-button size="small" icon="el-icon-zoom-in" :disabled="!ptzSupported || capLoading"
                   :class="{ 'is-holding': holding && holdCmd && holdCmd.key === 'zoomIn' }"
                   @pointerdown.native.prevent="pressStart(zoomCmd(1))"
                   @pointerup.native.prevent="onRelease"
                   @pointercancel.native="onRelease"
                   @pointerleave.native="onRelease">放大</el-button>
        <el-button size="small" icon="el-icon-zoom-out" :disabled="!ptzSupported || capLoading"
                   :class="{ 'is-holding': holding && holdCmd && holdCmd.key === 'zoomOut' }"
                   @pointerdown.native.prevent="pressStart(zoomCmd(-1))"
                   @pointerup.native.prevent="onRelease"
                   @pointercancel.native="onRelease"
                   @pointerleave.native="onRelease">缩小</el-button>
      </div>
    </div>

    <div class="ptz__preset" v-if="showPreset">
      <el-select v-model="presetId" size="mini" filterable placeholder="选择预置点" style="flex:1;min-width:0">
        <el-option v-for="p in presets" :key="p.id" :label="p.name" :value="p.id" />
      </el-select>
      <el-button size="mini" type="primary" :disabled="!presetId || !ptzSupported || capLoading" @click="doGotoPreset">调用</el-button>
    </div>


  </div>
</template>

<script>
import { getPresets, getPtzCapability, ptzContinuous, ptzStop, gotoPreset } from '@/api/video'
import appConfig from '@/config/app-config'

const STEP_MS = appConfig.video.stepDurationMs
const MAG = appConfig.video.stepMagnitude
const ZOOM_MAG = appConfig.video.zoomMagnitude
const HOLD_REPEAT_MS = 1000 // 长按期间心跳间隔（串行；频率过高会致设备指令排队/漂移）
const MAX_HOLD_MS = 60000   // 看门狗：单次长按最长时长，超时强制停止（防止释放丢失导致指令无限串流）
const MAX_FAILS = 3        // 连续失败熔断阈值（避免持续“打”设备）

function buildDirs(m) {
  return [
    { key: 'lt', short: '左上', icon: 'el-icon-top-left', pan: -m, tilt: m, zoom: 0 },
    { key: 'up', short: '上', icon: 'el-icon-top', pan: 0, tilt: m, zoom: 0 },
    { key: 'rt', short: '右上', icon: 'el-icon-top-right', pan: m, tilt: m, zoom: 0 },
    { key: 'lf', short: '左', icon: 'el-icon-back', pan: -m, tilt: 0, zoom: 0 },
    { key: 'st', short: '停', icon: 'el-icon-video-pause', pan: 0, tilt: 0, zoom: 0, isStop: true },
    { key: 'rg', short: '右', icon: 'el-icon-right', pan: m, tilt: 0, zoom: 0 },
    { key: 'lb', short: '左下', icon: 'el-icon-bottom-left', pan: -m, tilt: -m, zoom: 0 },
    { key: 'dn', short: '下', icon: 'el-icon-bottom', pan: 0, tilt: -m, zoom: 0 },
    { key: 'rb', short: '右下', icon: 'el-icon-bottom-right', pan: m, tilt: -m, zoom: 0 }
  ]
}

export default {
  name: 'PtzControlPanel',
  props: {
    nvrId: { type: [String, Number], required: true },
    channel: { type: [String, Number], required: true },
    nvrLabel: { type: String, default: '' },
    // 预置点：默认隐藏（暂时不展示）
    showPreset: { type: Boolean, default: false }
  },
  data() {
    return {
      ptzSupported: true,
      ptzTransient: false,
      ptzReason: '',
      capLoading: true,
      presets: [],
      presetId: null,
      holding: false,
      holdCmd: null,
      sending: false,
      failCount: 0,
      repeatTimer: null,
      stopTimer: null,
      watchdogTimer: null,
      pressAt: 0,
      isErr: false,
      logText: '就绪',
      lastCmd: '',
      dirs: buildDirs(MAG)
    }
  },
  watch: {
    nvrId() { this.onRelease(); this.loadCapability() },
    channel() { this.onRelease(); this.loadCapability() }
  },
  created() {
    this.loadCapability()
  },
  mounted() {
    window.addEventListener('pointerup', this.onRelease)
    window.addEventListener('pointercancel', this.onRelease)
    window.addEventListener('blur', this.onRelease)
    window.addEventListener('pagehide', this.onRelease)
    document.addEventListener('visibilitychange', this.onVisibility)
  },
  beforeDestroy() {
    window.removeEventListener('pointerup', this.onRelease)
    window.removeEventListener('pointercancel', this.onRelease)
    window.removeEventListener('blur', this.onRelease)
    window.removeEventListener('pagehide', this.onRelease)
    document.removeEventListener('visibilitychange', this.onVisibility)
    this.clearTimers()
    this.forceStop()
  },
  methods: {
    // 将“500/超时/网络错误”等映射为可读文案
    friendlyErr(e) {
      const m = (e && e.message) || String(e)
      if (/50\d|timeout|Network Error|ECONN|ETIMEDOUT|未响应/i.test(m)) {
        return '云台设备无响应（可能被限流/占用），请稍后重试'
      }
      return m
    },
    // 先探测云台能力；不支持则禁用控件（避免误操作与无谓报错）
    async loadCapability() {
      this.capLoading = true
      this.presets = []
      try {
        const res = await getPtzCapability(this.nvrId, this.channel)
        if (res && res.code === 200 && res.data) {
          // supported: true=支持 / false=确定不支持 / null=未知(瞬时故障)
          this.ptzSupported = res.data.supported !== false
          this.ptzTransient = res.data.supported === null || !!res.data.transient
          this.ptzReason = res.data.reason || ''
        } else {
          // 查询异常不武断禁用，允许重试
          this.ptzSupported = true
          this.ptzTransient = true
          this.ptzReason = (res && res.message) || '能力查询失败'
        }
      } catch (e) {
        this.ptzSupported = true
        this.ptzTransient = true
        this.ptzReason = this.friendlyErr(e)
      } finally {
        this.capLoading = false
      }
      if (this.ptzSupported) {
        this.loadPresets()
      }
    },
    async loadPresets() {
      try {
        const res = await getPresets(this.nvrId, this.channel)
        if (res && res.code === 200) {
          this.presets = res.data || []
          this.setLog('预置点加载成功：' + this.presets.length + ' 个', false)
        } else {
          this.presets = []
          this.setLog('预置点加载失败：' + ((res && res.message) || '设备无响应'), true)
        }
      } catch (e) {
        this.presets = []
        this.setLog('预置点加载失败：' + this.friendlyErr(e), true)
      }
    },
    setLog(t, err) { this.logText = t; this.isErr = !!err },
    zoomCmd(sign) {
      return { key: sign > 0 ? 'zoomIn' : 'zoomOut', short: sign > 0 ? '放大' : '缩小',
               pan: 0, tilt: 0, zoom: sign * ZOOM_MAG }
    },
    onVisibility() {
      if (document.hidden) this.onRelease()
    },
    clearTimers() {
      if (this.repeatTimer) { clearInterval(this.repeatTimer); this.repeatTimer = null }
      if (this.stopTimer) { clearTimeout(this.stopTimer); this.stopTimer = null }
      if (this.watchdogTimer) { clearTimeout(this.watchdogTimer); this.watchdogTimer = null }
    },
    // 按下：立即发一次 + 心跳重发（长按持续转动，串行发送）
    pressStart(cmd) {
      if (!cmd) return
      if (cmd.isStop) { this.clearTimers(); this.holding = false; this.holdCmd = null; this.forceStop(); return }
      this.clearTimers()
      this.holding = true
      this.holdCmd = cmd
      this.sending = false
      this.failCount = 0
      this.pressAt = Date.now()
      this.sendHold()
      this.repeatTimer = setInterval(this.sendHold, HOLD_REPEAT_MS)
      this.watchdogTimer = setTimeout(() => { this.onRelease() }, MAX_HOLD_MS)
      this.setLog(`方向[${cmd.short}] 连续转动中…（松开停止）`, false)
    },
    // 串行发送：上一次未返回则跳过本次
    sendHold() {
      const cmd = this.holdCmd
      if (!cmd || this.sending) return
      this.sending = true
      ptzContinuous(this.nvrId, this.channel, { pan: cmd.pan, tilt: cmd.tilt, zoom: cmd.zoom })
        .then(r => {
          if (r && r.code === 200 && r.data) {
            this.failCount = 0
            const code = r.data.nvrHttp
            this.lastCmd = `方向[${cmd.short}] → NVR ${code}`
            this.setLog(`方向[${cmd.short}] 连续转动中… → NVR ${code}`, false)
          } else {
            this.onHoldFail(cmd, (r && r.message) || '云台控制失败')
          }
        })
        .catch(e => { this.onHoldFail(cmd, this.friendlyErr(e)) })
        .finally(() => { this.sending = false })
    },
    // 连续失败达到阈值 -> 熔断（停止心跳与长按，避免持续“打”设备）
    onHoldFail(cmd, msg) {
      this.failCount += 1
      if (this.failCount >= MAX_FAILS) {
        this.clearTimers()
        this.holding = false
        this.holdCmd = null
        this.setLog(`方向[${cmd.short}] ${msg}（已连续失败 ${this.failCount} 次，停止重试）`, true)
      } else if (this.failCount === 1) {
        this.setLog(`方向[${cmd.short}] ${msg}`, true)
      }
    },
    // 松开/移出/取消：停止心跳 + 发送停止（短按保底 STEP_MS，形成“一步”）
    onRelease() {
      if (!this.holding) return
      const cmd = this.holdCmd
      const elapsed = Date.now() - this.pressAt
      this.holding = false
      this.holdCmd = null
      if (this.repeatTimer) { clearInterval(this.repeatTimer); this.repeatTimer = null }
      const wait = Math.max(0, STEP_MS - elapsed)
      if (this.stopTimer) { clearTimeout(this.stopTimer); this.stopTimer = null }
      this.stopTimer = setTimeout(() => { this.forceStop(cmd) }, wait)
    },
    async forceStop(cmd) {
      try {
        const r = await ptzStop(this.nvrId, this.channel)
        if (r && r.code === 200) {
          const prefix = cmd ? `方向[${cmd.short}] ` : ''
          this.setLog(prefix + (this.lastCmd ? this.lastCmd + ' | ' : '') + '已停止', false)
        } else {
          this.setLog('停止失败：' + ((r && r.message) || '设备无响应'), true)
        }
      } catch (e) {
        this.setLog('停止失败：' + this.friendlyErr(e), true)
      }
    },
    async doGotoPreset() {
      try {
        const r = await gotoPreset(this.nvrId, this.channel, this.presetId)
        if (r && r.code === 200 && r.data) {
          this.setLog(`调用预置点 ${this.presetId} → NVR ${r.data.nvrHttp}`, false)
        } else {
          this.setLog('调用预置点失败：' + ((r && r.message) || '设备无响应'), true)
        }
      } catch (e) {
        this.setLog('调用预置点失败：' + this.friendlyErr(e), true)
      }
    }
  }
}
</script>

<style scoped>
.ptz { color: #e2e8f0; }
.ptz__head { display: flex; align-items: baseline; justify-content: space-between; gap: 8px; margin-bottom: 12px; }
.ptz__title { font-size: 15px; font-weight: 600; white-space: nowrap; flex: 0 0 auto; }
.ptz__sub { font-size: 12px; color: #94a3b8; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; text-align: right; }
.ptz__pads { display: flex; flex-direction: column; gap: 12px; align-items: center; }
.ptz__grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 6px; width: 100%; max-width: 240px; }
.ptz__btn { width: 100%; min-width: 0; padding: 9px 2px; touch-action: none; user-select: none; }
.ptz__btn.is-holding { background:#2563eb; border-color:#2563eb; color:#fff; }
.ptz__zoom { display: flex; gap: 8px; }
.ptz__zoom .is-holding { background:#2563eb; border-color:#2563eb; color:#fff; }
.ptz__preset { display: flex; align-items: center; gap: 8px; margin-top: 12px; }
.ptz__notice { margin-bottom: 10px; font-size: 12px; color: #cbd5e1; background: #263243; border: 1px solid #334155; border-radius: 4px; padding: 6px 8px; }

/* 深色主题：统一 el-button / el-select / el-input 外观 */
.ptz ::v-deep .el-button {
  background: #334155; color: #cbd5e1; border-color: #475569;
}
.ptz ::v-deep .el-button:not(.is-disabled):hover,
.ptz ::v-deep .el-button:not(.is-disabled):focus {
  background: #3f4d63; color: #fff; border-color: #64748b;
}
.ptz ::v-deep .el-button.is-disabled,
.ptz ::v-deep .el-button.is-disabled:hover {
  background: #263243; color: #64748b; border-color: #334155;
}
.ptz ::v-deep .el-button--primary,
.ptz ::v-deep .el-button--primary:not(.is-disabled):hover {
  background: #2563eb; border-color: #2563eb; color: #fff;
}
.ptz ::v-deep .el-button.is-holding {
  background: #2563eb; border-color: #2563eb; color: #fff;
}
.ptz ::v-deep .el-input__inner {
  background: #334155; color: #e2e8f0; border-color: #475569;
}
.ptz ::v-deep .el-input__inner::placeholder { color: #94a3b8; }
</style>
